<script setup lang="ts">
import { useFetch } from '@/composables/useFetch';
import { usePermissions } from '@/composables/usePermissions';
// import { useSafeArea } from '@/composables/useSafeArea';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { Employee } from '@/types/api';
import type { BatchShiftCreationResponse } from '@/types/dayOffValidation';
import type { KitchenPosition, ShiftAssignment, ShiftSlot } from '@/types/employee';
import { formatDate } from '@/utils/date';
import { formatTimeRange } from '@/utils/time';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import UserBadge from '../Profile/UserBadge.vue';

const { t, te } = useI18n();
const { fetchData } = useFetch();
const envStore = useEnvStore();
const userStore = useUserStore();
const { can } = usePermissions();
const toast = useToast();

// Reactive variables for editing time slots
const editingTime = ref<Record<number, { start_time: string; end_time: string }>>({});
const isUpdatingTime = ref<Record<number, boolean>>({});

// Variables for tracking day off conflicts
const lastAssignmentResult = ref<{
  successful: Array<{ shift_date: string; shift_slot_id: number; status: string }>;
  failed: Array<{ shift_date: string; shift_slot_id: number; reason: string; day_off_details?: unknown }>;
} | null>(null);
const isAssigning = ref(false);

const props = defineProps<{
  visible: boolean;
  day?: string; // ISO date string
  shiftSlot?: ShiftSlot; // slot object { id, name }
  users?: Array<{ id: number; user: Employee; kitchen_position: KitchenPosition }>;
  employees: Employee[];
  kitchenPositions: KitchenPosition[];
  kitchenPositionsByCategory?: Record<string, KitchenPosition[]>;
  selectedSlots?: Array<{ day: string; slot: ShiftSlot }>;
  shiftsByDayAndSlot: Record<string, Record<string, ShiftAssignment[]>>;
  currentCategory?: string | null; // Keep this name for backward compatibility, but it now represents department
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'add'): void; // Simplified - just signal success, no payload needed
  (e: 'remove', payload: { shiftAssignmentId: number }): void;
  (e: 'update-time', payload: { shiftAssignmentId: number; start_time?: string; end_time?: string }): void;
}>();

// Filter positions by current department
const availablePositions = computed(() => {
  if (!props.currentCategory) {
    return props.kitchenPositions;
  }

  if (props.kitchenPositionsByCategory && props.kitchenPositionsByCategory[props.currentCategory]) {
    return props.kitchenPositionsByCategory[props.currentCategory];
  }

  return props.kitchenPositions.filter((pos) => pos.department?.name === props.currentCategory);
});

// Filter slot assignments by current department
const slotAssignments = computed(() => {
  if (!props.selectedSlots || !props.shiftsByDayAndSlot) return [];

  return props.selectedSlots.map(({ day, slot }) => {
    let assignments = props.shiftsByDayAndSlot[day]?.[slot.name] || [];

    // Filter assignments by current department if specified
    if (props.currentCategory) {
      assignments = assignments.filter((assignment) => assignment.kitchen_position?.department?.name === props.currentCategory);
    }

    return {
      day,
      slot,
      assignments,
    };
  });
});

const availableEmployees = computed(() => props.employees);

const showDialog = ref(props.visible);
watch(
  () => props.visible,
  (v) => (showDialog.value = v),
);
watch(showDialog, (v) => emit('update:visible', v));

const formUser = ref<Employee | null>(null);
const formPosition = ref<number | null>(null);
const formError = ref<string | null>(null);

async function handleAdd() {
  if (!can('employees_planning', 'create')) {
    formError.value = t('employee.slots.errors.noPermissionAssign');
    return;
  }
  if (!formUser.value || !formPosition.value) {
    formError.value = t('employee.slots.errors.selectEmployeeAndPosition');
    return;
  }
  if (!props.selectedSlots || props.selectedSlots.length === 0) {
    formError.value = t('employee.slots.errors.noSlotsSelected');
    return;
  }

  isAssigning.value = true;
  formError.value = null;
  lastAssignmentResult.value = null;

  try {
    // Make the batch assignment API call
    const currentRestaurantId = userStore.user?.restaurants[0]?.id || '';
    if (!currentRestaurantId) {
      formError.value = t('employee.slots.errors.noRestaurant');
      return;
    }

    const batchPayload = {
      restaurant_id: currentRestaurantId,
      restaurant_user_id: formUser.value.restaurant_user_id,
      kitchen_position_id: formPosition.value,
      user_id: formUser.value.id,
      assignments: props.selectedSlots.map(({ day, slot }) => ({
        shift_slot_id: slot.id,
        shift_date: day,
        type: 'assignment',
      })),
    };

    const { data, error } = await fetchData(`${envStore.apiUrl}/planning/employees/batch`, 'POST', batchPayload);

    if (error) {
      formError.value = error as string;
      return;
    }

    const response = data as BatchShiftCreationResponse;
    lastAssignmentResult.value = {
      successful: response.successful || [],
      failed: response.failed || [],
    };

    // Show appropriate toast messages
    if (response.successful?.length > 0) {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('employee.slots.assignment.successMessage', { count: response.successful.length }),
        life: 5000,
      });
    }

    if (response.failed?.length > 0) {
      const dayOffFailed = response.failed.filter((f) => f.reason === 'day_off');
      if (dayOffFailed.length > 0) {
        toast.add({
          severity: 'warn',
          summary: t('employee.slots.assignment.partialSuccess'),
          detail: t('employee.slots.assignment.dayOffConflicts', { count: dayOffFailed.length }),
          life: 8000,
        });
      }

      // Show other failure reasons
      const otherFailed = response.failed.filter((f) => f.reason !== 'day_off');
      if (otherFailed.length > 0) {
        toast.add({
          severity: 'error',
          summary: t('employee.slots.assignment.failed'),
          detail: t('employee.slots.assignment.otherFailures', { count: otherFailed.length }),
          life: 8000,
        });
      }
    }

    // Emit success event to parent to refresh data (no payload needed)
    emit('add');

    // Clear form if all assignments were successful
    if (!response.failed?.length) {
      formUser.value = null;
      formPosition.value = null;
    }
  } catch (err) {
    console.error('Error in batch assignment:', err);
    formError.value = t('employee.slots.errors.unexpectedError');

    // Show error toast
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: formError.value,
      life: 5000,
    });
  } finally {
    isAssigning.value = false;
  }
}

const confirm = useConfirm();

function confirmRemove(shiftAssignmentId: number, userName: string) {
  if (!can('employees_planning', 'delete')) return;
  confirm.require({
    message: t('employee.slots.removeUser.confirmLabel', { name: userName }),
    header: t('employee.slots.removeUser.confirmHeader'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('employee.slots.removeUser.confirm'),
    accept: () => {
      emit('remove', { shiftAssignmentId });
    },
    reject: () => {},
  });
}

function startEditingTime(assignment: ShiftAssignment) {
  if (!can('employees_planning', 'update')) return;
  editingTime.value[assignment.id] = {
    start_time: assignment.start_time || '',
    end_time: assignment.end_time || '',
  };
}

function cancelEditingTime(assignmentId: number) {
  delete editingTime.value[assignmentId];
}

async function updateAssignmentTime(assignmentId: number) {
  if (!can('employees_planning', 'update')) return;
  const timeData = editingTime.value[assignmentId];
  if (!timeData) return;

  isUpdatingTime.value[assignmentId] = true;

  try {
    await fetchData(`${envStore.apiUrl}/planning/employees/${assignmentId}`, 'PATCH', {
      start_time: timeData.start_time || null,
      end_time: timeData.end_time || null,
    });

    emit('update-time', {
      shiftAssignmentId: assignmentId,
      start_time: timeData.start_time,
      end_time: timeData.end_time,
    });

    delete editingTime.value[assignmentId];
  } catch (error) {
    console.error('Error updating assignment time:', error);
  } finally {
    isUpdatingTime.value[assignmentId] = false;
  }
}

function isEditingTime(assignmentId: number): boolean {
  return assignmentId in editingTime.value;
}
</script>

<template>
  <Dialog
    v-model:visible="showDialog"
    modal
    :header="props.day && props.shiftSlot ? `${props.day} - ${props.shiftSlot?.name}` : t('employee.planning.batchAssign.label')"
    :style="{ width: '40rem', maxWidth: '90vw' }"
  >
    <div class="relative flex flex-col gap-4">
      <div>
        <div v-if="props.users && props.users.length" class="mt-4">
          <div v-for="userObj in props.users" :key="userObj.user.id" class="mb-2 flex items-center gap-2">
            <span>{{ userObj.user.first_name }} ({{ userObj.kitchen_position?.name }})</span>
            <Button icon="pi pi-trash" severity="danger" text size="small" @click="confirmRemove(userObj.id, userObj.user.first_name)" />
          </div>
        </div>
        <div v-if="slotAssignments.length" class="mb-4 flex flex-col gap-2 overflow-y-auto">
          <div v-for="item in slotAssignments" :key="item.day + '-' + item.slot.id" class="mb-2 flex flex-col gap-2">
            <div class="font-semibold">
              {{ formatDate(new Date(item.day)) }} -
              {{ te(`employee.slots.timeSlots.${item.slot.name}`) ? t(`employee.slots.timeSlots.${item.slot.name}`) : item.slot.name }}
            </div>
            <div v-if="item.assignments.length">
              <ul class="ml-4 flex flex-col gap-4">
                <li v-for="a in item.assignments" :key="a.id" class="flex flex-col gap-2">
                  <div class="flex w-full items-center gap-2">
                    <UserBadge
                      :user="a.user"
                      :color="a.kitchen_position?.color"
                      show-name
                      :show-trash="can('employees_planning', 'delete')"
                      :show-edit="!isEditingTime(a.id) && can('employees_planning', 'update')"
                      :accent="!!a.start_time || !!a.end_time"
                      @trash="confirmRemove(a.id, a.user.first_name)"
                      @edit="startEditingTime(a)"
                    />
                    <span v-if="!a.kitchen_position?.name" class="text-xs text-gray-500">({{ t('employee.slots.noPosition') }})</span>
                  </div>
                  <div>
                    <div v-if="!isEditingTime(a.id) && (a.start_time || a.end_time)" class="flex items-center gap-2">
                      <span class="text-xs text-gray-600">
                        {{ a.start_time || a.end_time ? formatTimeRange(a.start_time, a.end_time, t) : t('employee.slots.noTimeSet') }}
                      </span>
                    </div>

                    <div v-if="isEditingTime(a.id) && can('employees_planning', 'update')" class="flex flex-wrap items-end gap-2">
                      <div class="flex flex-col gap-1">
                        <label :for="`input-start-time-${a.id}`" class="text-xs">{{ t('employee.slots.startTime') }}:</label>
                        <InputText
                          :id="`input-start-time-${a.id}`"
                          v-model="editingTime[a.id].start_time"
                          type="time"
                          size="small"
                          class="w-24 text-xs"
                        />
                      </div>
                      <div class="flex flex-col gap-1">
                        <label :for="`input-end-time-${a.id}`" class="text-xs">{{ t('employee.slots.endTime') }}:</label>
                        <InputText
                          :id="`input-end-time-${a.id}`"
                          v-model="editingTime[a.id].end_time"
                          type="time"
                          size="small"
                          class="w-24 text-xs"
                        />
                      </div>
                      <div class="flex flex-row items-center justify-between gap-2">
                        <Button
                          icon="pi pi-check"
                          size="small"
                          text
                          severity="success"
                          :loading="isUpdatingTime[a.id]"
                          @click="updateAssignmentTime(a.id)"
                        />
                        <Button icon="pi pi-times" size="small" text severity="secondary" @click="cancelEditingTime(a.id)" />
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div v-else class="ml-4 text-gray-500">
              {{ t('employee.slots.noUsersAssigned') }}
            </div>
          </div>
        </div>

        <!-- Day off conflict results -->
        <div v-if="lastAssignmentResult && lastAssignmentResult.failed.length > 0" class="mb-4 rounded border-l-4 border-yellow-400 bg-yellow-50 p-4">
          <h4 class="font-semibold text-yellow-800">{{ t('employee.slots.assignment.conflicts') }}</h4>
          <div class="mt-2 space-y-2">
            <div v-for="failure in lastAssignmentResult.failed" :key="`${failure.shift_date}-${failure.shift_slot_id}`" class="text-sm">
              <div class="font-medium text-yellow-800">
                {{ formatDate(new Date(failure.shift_date)) }} -
                {{
                  props.selectedSlots?.find((s) => s.day === failure.shift_date && s.slot.id === failure.shift_slot_id)?.slot.name ||
                  t('employee.slots.unknownSlot')
                }}
              </div>
              <div class="text-yellow-700">
                {{ t('employee.slots.assignment.reason') }}:
                {{ failure.reason === 'day_off' ? t('employee.slots.assignment.employeeHasDayOff') : failure.reason }}
              </div>
              <div v-if="failure.day_off_details && (failure.day_off_details as any).status" class="text-sm text-yellow-600">
                {{ t('employee.slots.assignment.dayOffStatus') }}: {{ t(`dayOff.status.${(failure.day_off_details as any).status}`) }}
                <span v-if="(failure.day_off_details as any).reason"> ({{ t(`dayOff.reasons.${(failure.day_off_details as any).reason}`) }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="can('employees_planning', 'create')" class="sticky -bottom-3 border-t bg-white py-4">
        <form class="flex flex-wrap items-end gap-4" @submit.prevent="handleAdd">
          <div class="flex flex-col gap-2">
            <label class="text-xs">{{ t('employee.form.employee') }}</label>
            <Dropdown
              v-model="formUser"
              :options="availableEmployees"
              option-label="first_name"
              :placeholder="t('employee.select')"
              class="w-full min-w-[180px]"
              required
            >
              <template #option="slotProps">
                <span>{{ slotProps.option.first_name }} {{ slotProps.option.last_name }}</span>
              </template>
              <template #value="slotProps">
                <span v-if="slotProps.value"> {{ slotProps.value.first_name }} {{ slotProps.value.last_name }} </span>
                <span v-else>
                  {{ t('employee.form.select') }}
                </span>
              </template>
            </Dropdown>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-xs">{{ t('employee.form.position') }}</label>
            <Dropdown
              v-model="formPosition"
              :options="availablePositions"
              option-label="name"
              option-value="id"
              :placeholder="t('employee.kitchenPositions.select')"
              class="w-full min-w-[180px]"
            >
              <template #value="slotProps">
                <span v-if="slotProps.value">
                  <span
                    class="mr-2 inline-block h-3 w-3 rounded-full align-middle"
                    :style="{
                      backgroundColor: '#' + (availablePositions.find((kp) => kp.id === slotProps.value)?.color || 'cccccc'),
                    }"
                  ></span>
                  {{ t('employee.kitchenPositions.positions.' + (availablePositions.find((kp) => kp.id === slotProps.value)?.name || '')) }}
                </span>
                <span v-else>
                  {{ t('employee.kitchenPositions.select') }}
                </span>
              </template>
              <template #option="slotProps">
                <span>
                  <span class="mr-2 inline-block h-3 w-3 rounded-full align-middle" :style="{ backgroundColor: '#' + slotProps.option.color }"></span>
                  {{ t('employee.kitchenPositions.positions.' + slotProps.option.name) }}
                </span>
              </template>
            </Dropdown>
          </div>
          <Button type="submit" :label="t('employee.form.submit')" size="small" :loading="isAssigning" />
          <span v-if="formError" class="text-red-600">{{ formError }}</span>
        </form>
      </div>
    </div>
  </Dialog>
</template>
